import { Color, Quality } from 'common';
import Data from 'data';
import Locale from 'data/locale';
import { isSubType, presetItems } from './itemTypes';

const _L = Locale("ui-item");

export function formatStr(fmt, ...values) {
  let i = 0;
  return fmt.replace(/%(\+)?([ids%\d])/g, (m, plus, chr) => {
    if (chr === '%') {
      return chr;
    } else {
      let value = (chr === 'd' || chr === 's' || chr === 'i' ? values[i++] : values[chr]);
      if (plus && !isNaN(value) && parseInt(value) > 0) value = '+' + value;
      return value;
    }
  });
}

export function addValues(dst, src) {
  const dobj = (dst && typeof dst === 'object'), sobj = (src && typeof src === 'object');
  if (dobj || sobj) {
    if (!dst) return {...src};
    if (!src) return dst;
    if (!dobj) dst = {min: dst, max: dst};
    if (sobj) {
      dst.min += src.min;
      dst.max += src.max;
    } else {
      dst.min += src;
      dst.max += src;
    }
    return dst;
  } else if (dst) {
    return dst + (src || 0);
  } else {
    return src;
  }
}

function addProp(stats, id, param, min, max, value, item) {
  const {ilvl = 99, base} = (item || {});
  const prop = Data.properties[id];
  if (!prop) return;
  let last = 0;
  function addStat(stat, param, value) {
    last = value;
    if (param != null) stat = `${stat}#${param}`;
    if (stat === 'item_numsockets' && !isNaN(value) && item) {
      value = Math.min(value, itemMaxSockets(Data.items[item.base]));
    }
    stats[stat] = addValues(stats[stat], value);
    if (stat === 'poisonlength') {
      stats.poison_count = (stats.poison_count || 0) + 1;
    }
  }
  function statFunc(set, val, func, stat) {
    switch (func) {
    case 3:
      addStat(stat, null, last);
      break;
    case 5:
      // technically this should add secondary_mindamage to 2h items
      addStat('mindamage', null, value);
      break;
    case 6:
      // technically this should add secondary_maxdamage to 2h items
      addStat('maxdamage', null, value);
      break;
    case 7:
      // add +1 max instead if base damage is too low
      if (!isNaN(value)) {
        const baseItem = Data.items[base];
        // how does it work with ethereal?
        if (baseItem && isSubType(baseItem.type, 'weap') && (baseItem['2handmaxdam'] || baseItem.maxdam || 0) * value < 100) {
          addStat('maxdamage', null, 1);
        } else {
          addStat('item_mindamage_percent', null, value);
          addStat('item_maxdamage_percent', null, value);
        }
      } else {
        // perhaps add +1max as well for parsing? need some logic here
        addStat('item_mindamage_percent', null, value);
        addStat('item_maxdamage_percent', null, value);
      }
      break;
    case 10:
      addStat(stat, param, value);
      break;
    case 11:
      if (max === 0) {
        const reqlvl = Data.skills[param].reqlevel;
        max = Math.round((ilvl - reqlvl + 1) / 3.9);
        addStat(stat, `${param}#${min}#x`, max);
      } else {
        addStat(stat, `${param}#${min}`, max);
      }
      break;
    case 12:
      addStat(stat, `${param}#x`, value);
      break;
    case 14:
      addStat(stat, null, param != null ? param : value);
      break;
    case 15:
      addStat(stat, null, min);
      break;
    case 16:
      addStat(stat, null, max);
      break;
    case 17:
      addStat(stat, null, param != null ? param : value);
      break;
    case 19:
      if (min < 0 && max < 0 && Data.skills[param]) {
        const reqlvl = Data.skills[param].reqlevel;
        const slvl = Math.max(1, Math.floor((ilvl - reqlvl) / Math.floor((99 - reqlvl) / (-max))));
        const charges = Math.floor(-min * slvl / 8) + (-min);
        addStat(stat, `${param}#${slvl}`, charges);
      } else {
        addStat(stat, `${param}#${max}`, min);
      }
      break;
    case 20:
      stats.item_indesctructible = 1;
      break;
    case 21:
      addStat(stat, val, value);
      break;
    case 22:
    case 24:
      addStat(stat, param, value);
      break;
    case 23:
      stats.item_ethereal = 1;
      break;
    case 36:
      addStat(stat, `${val}#x`, value);
      break;
    default:
      addStat(stat, null, set == null ? value : set);
      break;
    }
  }
  if (prop.func1) statFunc(prop.set1, prop.val1, prop.func1, prop.stat1);
  if (prop.func2) statFunc(prop.set2, prop.val2, prop.func2, prop.stat2);
  if (prop.func3) statFunc(prop.set3, prop.val3, prop.func3, prop.stat3);
  if (prop.func4) statFunc(prop.set4, prop.val4, prop.func4, prop.stat4);
  if (prop.func5) statFunc(prop.set5, prop.val5, prop.func5, prop.stat5);
  if (prop.func6) statFunc(prop.set6, prop.val6, prop.func6, prop.stat6);
  if (prop.func7) statFunc(prop.set7, prop.val7, prop.func7, prop.stat7);
}

export const itemAddProp = addProp;

export function modsToStats(stats, mods, item) {
  function addMod(mod, values) {
    const affix = Data.mods[mod];
    if (!affix) return;
    if (affix.mod1code) addProp(stats, affix.mod1code, affix.mod1param, affix.mod1min, affix.mod1max, values[0], item);
    if (affix.mod2code) addProp(stats, affix.mod2code, affix.mod2param, affix.mod2min, affix.mod2max, values[1], item);
    if (affix.mod3code) addProp(stats, affix.mod3code, affix.mod3param, affix.mod3min, affix.mod3max, values[2], item);
    if (affix.mod4code) addProp(stats, affix.mod4code, affix.mod4param, affix.mod4min, affix.mod4max, values[3], item);
    if (affix.mod5code) addProp(stats, affix.mod5code, affix.mod5param, affix.mod5min, affix.mod5max, values[4], item);
  }

  const numpsn = stats.poison_count || 0;
  for (const mod in mods) {
    addMod(mod, mods[mod]);
  }
  if (stats.poison_count) {
    stats.poison_count = Math.min(stats.poison_count, numpsn + 1);
  }

  return stats;
}

export function uniqueStats(stats, uniq, values, item) {
  for (let i = 1; i <= 12; ++i) {
    if (uniq[`prop${i}`]) {
      const min = uniq[`min${i}`], max = uniq[`max${i}`];
      addProp(stats, uniq[`prop${i}`], uniq[`par${i}`], min, max, values ? values[i - 1] : (min === max ? max : ''), item);
    }
  }
  if (!uniq.addfunc && uniq.set) {
    for (let i = 1; i <= 5; ++i) {
      if (uniq[`aprop${i}a`]) addProp(stats, uniq[`aprop${i}a`], uniq[`apar${i}a`], uniq[`amin${i}a`], uniq[`amax${i}a`], uniq[`amax${i}a`], item);
      if (uniq[`aprop${i}b`]) addProp(stats, uniq[`aprop${i}b`], uniq[`apar${i}b`], uniq[`amin${i}b`], uniq[`amax${i}b`], uniq[`amax${i}b`], item);
    }
  }
  return stats;
}

export function itemsetPartStats(uniq, equipped, index, item) {
  const stats = [];
  if (!uniq.addfunc || index < 0) return stats;
  const count = equipped.reduce((sum, cur) => sum + (cur ? 1 : 0), 0);
  equipped.splice(index, 1);
  for (let i = 5; i >= 1; --i) {
    if (uniq.addfunc === 2 && count <= i) continue;
    if (uniq.addfunc === 1 && !equipped[equipped.length - i]) continue;
    const curst = {};
    if (uniq[`aprop${i}a`]) addProp(curst, uniq[`aprop${i}a`], uniq[`apar${i}a`], uniq[`amin${i}a`], uniq[`amax${i}a`], uniq[`amax${i}a`], item);
    if (uniq[`aprop${i}b`]) addProp(curst, uniq[`aprop${i}b`], uniq[`apar${i}b`], uniq[`amin${i}b`], uniq[`amax${i}b`], uniq[`amax${i}b`], item);
    if (Object.keys(curst).length) stats.push({stats: curst, index: i});
  }
  return stats;
}

export function itemsetStats(stats, set, count, maxItems) {
  const maxBonus = Math.min(5, count, maxItems - 1);
  for (let i = 2; i <= maxBonus; ++i) {
    if (set[`pcode${i}a`]) addProp(stats, set[`pcode${i}a`], set[`pparam${i}a`], set[`pmin${i}a`], set[`pmax${i}a`], set[`pmax${i}a`]);
    if (set[`pcode${i}b`]) addProp(stats, set[`pcode${i}b`], set[`pparam${i}b`], set[`pmin${i}b`], set[`pmax${i}b`], set[`pmax${i}b`]);
  }
  if (count >= maxItems) {
    for (let i = 1; i <= 8; ++i) {
      if (set[`fcode${i}`]) addProp(stats, set[`fcode${i}`], set[`fparam${i}`], set[`fmin${i}`], set[`fmax${i}`], set[`fmax${i}`]);
    }
  }
  return stats;
}

export function runewordStats(stats, uniq, values, item) {
  for (let i = 1; i <= 7; ++i) {
    if (uniq[`t1code${i}`] && uniq[`t1code${i}`] !== 'ethereal') {
      const min = uniq[`t1min${i}`], max = uniq[`t1max${i}`];
      addProp(stats, uniq[`t1code${i}`], uniq[`t1param${i}`], min, max, values ? values[i - 1] : (min === max ? max : ''), item);
    }
  }
  return stats;
}

export function gemStats(stats, code, types) {
  const gem = Data.gems[code];
  if (!gem) return stats;
  if (types.has('weap')) {
    if (gem.weaponmod1code) addProp(stats, gem.weaponmod1code, gem.weaponmod1param, gem.weaponmod1min, gem.weaponmod1max, gem.weaponmod1max);
    if (gem.weaponmod2code) addProp(stats, gem.weaponmod2code, gem.weaponmod2param, gem.weaponmod2min, gem.weaponmod2max, gem.weaponmod2max);
    if (gem.weaponmod3code) addProp(stats, gem.weaponmod3code, gem.weaponmod3param, gem.weaponmod3min, gem.weaponmod3max, gem.weaponmod3max);
  } else if (types.has('shld')) {
    if (gem.shieldmod1code) addProp(stats, gem.shieldmod1code, gem.shieldmod1param, gem.shieldmod1min, gem.shieldmod1max, gem.shieldmod1max);
    if (gem.shieldmod2code) addProp(stats, gem.shieldmod2code, gem.shieldmod2param, gem.shieldmod2min, gem.shieldmod2max, gem.shieldmod2max);
    if (gem.shieldmod3code) addProp(stats, gem.shieldmod3code, gem.shieldmod3param, gem.shieldmod3min, gem.shieldmod3max, gem.shieldmod3max);
  } else if (types.has('helm') || types.has('tors')) {
    if (gem.helmmod1code) addProp(stats, gem.helmmod1code, gem.helmmod1param, gem.helmmod1min, gem.helmmod1max, gem.helmmod1max);
    if (gem.helmmod2code) addProp(stats, gem.helmmod2code, gem.helmmod2param, gem.helmmod2min, gem.helmmod2max, gem.helmmod2max);
    if (gem.helmmod3code) addProp(stats, gem.helmmod3code, gem.helmmod3param, gem.helmmod3min, gem.helmmod3max, gem.helmmod3max);
  }
  return stats;
}

export function statsToLines0(stats, charData) {
  const statLines = [];

  // special stats for buffs
  if (stats.skill_armor_percent) {
    stats.item_armor_percent = (stats.item_armor_percent || 0) + stats.skill_armor_percent;
  }
  if (stats.skill_poison_override_length) {
    stats.poisonlength = stats.skill_poison_override_length;
    delete stats.skill_poison_override_length;
  }
  if (stats.damagepercent) {
    stats.item_mindamage_percent = (stats.item_mindamage_percent || 0) + stats.damagepercent;
    stats.item_maxdamage_percent = (stats.item_maxdamage_percent || 0) + stats.damagepercent;
    delete stats.damagepercent;
  }
  if (stats.shrine_tohit_percent) {
    stats.item_tohit_percent = (stats.item_tohit_percent || 0) + stats.shrine_tohit_percent;
    delete stats.shrine_tohit_percent;
  }
  if (stats.velocitypercent) {
    stats.item_fastermovevelocity = (stats.item_fastermovevelocity || 0) + stats.velocitypercent;
    delete stats.velocitypercent;
  }
  if (stats.attackrate) {
    stats.item_fasterattackrate = (stats.item_fasterattackrate || 0) + stats.attackrate;
    delete stats.attackrate;
  }

  if (stats.skill_staminapercent) {
    statLines.push([Data.itemStatCost.item_maxmana_percent.descpriority, `Increase Maximum Stamina ${stats.skill_staminapercent}%`]);
    delete stats.skill_staminapercent;
  }
  if (stats.curse_resistance) {
    statLines.push([0, Data.info.legacyFormat ? `${Locale.strings.StrSkill85}${stats.curse_resistance}%` : formatStr(Locale.strings.StrSkill85, stats.curse_resistance)]);
    delete stats.curse_resistance;
  }
  if (stats.bonearmor) {
    const value = Math.round(stats.bonearmor / 256);
    statLines.push([0, Data.info.legacyFormat ? `${Locale.strings.StrSkill40}${value}${Locale.strings.StrSkill41}` : formatStr(Locale.strings.StrSkill40, value)]);
    delete stats.bonearmor;
  }

  if (stats.strength && stats.energy === stats.strength && stats.dexterity === stats.strength && stats.vitality === stats.strength) {
    let value = stats.strength;
    if (Data.info.legacyFormat) {
      if (isNaN(value) || value >= 0) value = "+" + value;
      statLines.push([Data.itemStatCost.energy.descpriority, `${value} ${Locale.strings.Moditem2allattrib}`]);
    } else {
      if (isNaN(value)) value = "+" + value;
      statLines.push([Data.itemStatCost.energy.descpriority, formatStr(Locale.strings.Moditem2allattrib, value)]);
    }
    delete stats.strength;
    delete stats.energy;
    delete stats.dexterity;
    delete stats.vitality;
  }

  if (stats.fireresist && stats.lightresist === stats.fireresist && stats.coldresist === stats.fireresist && stats.poisonresist === stats.fireresist) {
    let value = stats.fireresist;
    if (isNaN(value) && !Data.info.legacyFormat) value = "+" + value;
    statLines.push([Data.itemStatCost.poisonresist.descpriority, formatStr(Locale.strings.strModAllResistances, value)]);
    delete stats.fireresist;
    delete stats.lightresist;
    delete stats.coldresist;
    delete stats.poisonresist;
  }

  if (stats.mindamage && stats.maxdamage) {
    if (stats.mindamage === stats.maxdamage) {
      statLines.push([Data.itemStatCost.maxdamage.descpriority, formatStr(Locale.strings.strModMinDamage, stats.mindamage)]);
    } else {
      statLines.push([Data.itemStatCost.maxdamage.descpriority, formatStr(Locale.strings.strModMinDamageRange, stats.mindamage, stats.maxdamage)]);
    }
    delete stats.mindamage;
    delete stats.maxdamage;
  }

  if (stats.firemindam && stats.firemaxdam) {
    if (stats.firemindam === stats.firemaxdam) {
      statLines.push([Data.itemStatCost.firemaxdam.descpriority, formatStr(Locale.strings.strModFireDamage, stats.firemindam)]);
    } else {
      statLines.push([Data.itemStatCost.firemaxdam.descpriority, formatStr(Locale.strings.strModFireDamageRange, stats.firemindam, stats.firemaxdam)]);
    }
    delete stats.firemindam;
    delete stats.firemaxdam;
  }

  if (stats.coldmindam && stats.coldmaxdam) {
    if (stats.coldmindam === stats.coldmaxdam) {
      statLines.push([Data.itemStatCost.coldmaxdam.descpriority, formatStr(Locale.strings.strModColdDamage, stats.coldmindam)]);
    } else {
      statLines.push([Data.itemStatCost.coldmaxdam.descpriority, formatStr(Locale.strings.strModColdDamageRange, stats.coldmindam, stats.coldmaxdam)]);
    }
    delete stats.coldmindam;
    delete stats.coldmaxdam;
    delete stats.coldlength;
  }

  if (stats.lightmindam && stats.lightmaxdam) {
    if (stats.lightmindam === stats.lightmaxdam) {
      statLines.push([Data.itemStatCost.lightmaxdam.descpriority, formatStr(Locale.strings.strModLightningDamage, stats.lightmindam)]);
    } else {
      statLines.push([Data.itemStatCost.lightmaxdam.descpriority, formatStr(Locale.strings.strModLightningDamageRange, stats.lightmindam, stats.lightmaxdam)]);
    }
    delete stats.lightmindam;
    delete stats.lightmaxdam;
  }

  if (stats.magicmindam && stats.magicmaxdam) {
    if (stats.magicmindam === stats.magicmaxdam) {
      statLines.push([Data.itemStatCost.magicmaxdam.descpriority, formatStr(Locale.strings.strModMagicDamage, stats.magicmindam)]);
    } else {
      statLines.push([Data.itemStatCost.magicmaxdam.descpriority, formatStr(Locale.strings.strModMagicDamageRange, stats.magicmindam, stats.magicmaxdam)]);
    }
    delete stats.magicmindam;
    delete stats.magicmaxdam;
  }

  if (stats.poisonmindam && stats.poisonmaxdam && stats.poisonlength) {
    let length = stats.poisonlength, lenStr;
    if (isNaN(length)) {
      lenStr = length.replace?.(/{(\d+)}/, '{$1;length}');
    } else {
      if (stats.poison_count) length /= stats.poison_count;
      lenStr = Math.round(length / 25);
    }
    const scale = value => {
      if (isNaN(value)) {
        const m = value.match(/{(\d+)}/);
        if (m) return `{${m[1]};poison}`;
      } else {
        return Math.round(value * length / 256);
      }
    };
    if (stats.poisonmindam === stats.poisonmaxdam) {
      statLines.push([Data.itemStatCost.poisonmaxdam.descpriority, formatStr(Locale.strings.strModPoisonDamage, scale(stats.poisonmindam), lenStr)]);
    } else {
      statLines.push([Data.itemStatCost.poisonmaxdam.descpriority, formatStr(Locale.strings.strModPoisonDamageRange, scale(stats.poisonmindam), scale(stats.poisonmaxdam), lenStr)]);
    }
    delete stats.poisonmindam;
    delete stats.poisonmaxdam;
    delete stats.poisonlength;
    delete stats.poison_count;
  }

  if (stats.item_mindamage_percent && stats.item_maxdamage_percent === stats.item_mindamage_percent) {
    let value = stats.item_mindamage_percent;
    if (Data.info.legacyFormat) {
      if (isNaN(value) || value >= 0) value = "+" + value;
      statLines.push([Data.itemStatCost.item_mindamage_percent.descpriority, `${value}% ${Locale.strings.strModEnhancedDamage}`]);
    } else {
      if (isNaN(value)) value = "+" + value;
      statLines.push([Data.itemStatCost.item_mindamage_percent.descpriority, formatStr(Locale.strings.strModEnhancedDamage, value)]);
    }
    delete stats.item_mindamage_percent;
    delete stats.item_maxdamage_percent;
  }
  for (const idFull in stats) {
    let [id, param, param2] = idFull.split('#');
    const stat = Data.itemStatCost[id];
    if (!stat || !stat.descfunc) continue;
    let value = stats[idFull];
    if (!value && param2 !== 'x') continue;
    let priority = stat.descpriority;
    if (param) priority += parseInt(param) * 0.000001;
    if (param2 === 'x') {
      param2 = param;
      param = value;
      value = param2;
      param2 = null;
    }
    let str = Locale.strings[isNaN(value) || value >= 0 ? stat.descstrpos : stat.descstrneg];
    let str2 = ((stat.descfunc >= 6 && stat.descfunc <= 10) || stat.descfunc === 19 || stat.descfunc === 21 || stat.descfunc === 26) && Locale.strings[stat.descstr2];
    const skillName = id => {
      if (isNaN(id)) {
        if (typeof id === 'string') {
          const m = id.match(/{(\d+)}/);
          if (m) return `{${m[1]};skill}`;
        }
        return _L`Random Skill`;
      }
      const skill = Data.skills[id];
      const skilldesc = skill && Data.skillDesc[skill.skilldesc];
      return Locale.strings[skilldesc?.strname];
    };
    const skillClass = id => {
      if (isNaN(id)) {
        if (typeof id === 'string') {
          const m = id.match(/{(\d+)}/);
          if (m) return `{${m[1]};skillclass}`;
        }
        return '';
      }
      const skill = Data.skills[id];
      if (!skill.charclass) return '';
      return Locale.istrings[Data.strClassOnly[Data.classes.indexOf(skill.charclass)]];
    };
    let m = null;
    //if (stat.descfunc >= 6 && stat.descfunc <= 10) {
    if (stat.op >= 2 && stat.op <= 5) {
      if (isNaN(value)) {
        m = value.match(/{(\d+)}/);
        if (m) value = `{${m[1]};lvl}`;
      } else {
        value = (value * (charData?.level || 99) / (1 << stat.opparam)) | 0;
      }
    }
    switch (stat.descfunc) {
    case 1: case 6: // +[value]
      if (isNaN(value) || value >= 0) value = "+" + value;
      break;
    case 2: case 7: // [value]%
      value += "%";
      break;
    case 3: case 9: // [value]
      break;
    case 4: case 8: // +[value]%
      if (isNaN(value) || value >= 0) value = "+" + value;
      value += "%";
      break;
    case 5: case 10: // [value*100/128]%
      if (isNaN(value)) {
        m = value.match(/{(\d+)}/);
        if (m) value = `{${m[1]};per128}`;
      } else {
        value = (value * 100 / 128) | 0;
      }
      if (Data.info.legacyFormat) {
        value += "%";
      } else {
        str = formatStr(str, value);
      }
      break;
    case 11: // Repairs 1 Durability In [100 / value] Seconds
      if (isNaN(value)) {
        m = value.match(/{(\d+)}/);
        if (m) value = `{${m[1]};div100}`;
      } else {
        value = (100 / value) | 0;
      }
      str = formatStr(Locale.strings.ModStre9u, 1, value);
      break;
    case 12: // +[value] if value is not 1
      if (value === 1) {
        value = '';
      } else {
        if (isNaN(value) || value >= 0) value = "+" + value;
      }
      break;
    case 13: // +[value] to [class] Skill Levels
      if (isNaN(value) || (Data.info.legacyFormat && value >= 0)) value = "+" + value;
      // here param can be value
      if (isNaN(param)) {
        m = param.match?.(/{(\d+)}/);
        if (m) {
          str = `{${m[1]};classskills}`;
        } else {
          str = _L`to Random Class Skills`;
        }
        if (!Data.info.legacyFormat) {
          if (!isNaN(value) && parseInt(value) > 0) value = '+' + value;
          str = `${value} ${str}`;
        }
      } else if (Data.info.legacyFormat) {
        str = Locale.istrings[Data.strClassSkillsStat[param]];
      } else {
        str = formatStr(Locale.istrings[Data.strClassSkillsStat[param]], value);
      }
      break;
    case 14: // +[value] to [skilltab] Skill Levels ([class] Only)
      str = formatStr(Locale.istrings[Data.strSkilltabStat[param]], value);
      str2 = Locale.istrings[Data.strClassOnly[Math.floor(param / 3)]];
      break;
    case 15: // [chance]% to cast [slvl] [skill] on [event]
      str = formatStr(str, param2, value, skillName(param));
      break;
    case 16: // Level [sLvl] [skill] Aura When Equipped
      str = formatStr(str, value, skillName(param));
      break;
    case 19:
      str = formatStr(str, value);
      break;
    case 20: // [-value]%
      if (isNaN(value)) {
        value = "-" + value;
      } else {
        value = -value;
      }
      value += "%";
      break;
    case 21: // [-value]
      if (isNaN(value)) {
        value = "-" + value;
      } else {
        value = -value;
      }
      break;
    case 22: // [value]% / [montype]
      value += "%";
      // replace param with monster type
      str += " " + param;
      break;
    case 23: // [value]% / [montype]
      param = Data.monsterById[param]?.namestr;
      if (Data.info.legacyFormat) {
        value += "%";
        str += " " + (Locale.strings[param] || param);
      } else {
        str = formatStr(str, value, Locale.strings[param] || param);
      }
      break;
    case 24: // charges
      if (Data.info.legacyFormat) {
        str = `${Locale.strings.skilldesc1}${param2} ${skillName(param)} ${formatStr(str, value, value)}`;
      } else {
        str = formatStr(str, param2, skillName(param), value, value);
      }
      break;
    case 27: // +[value] to [skill] ([class] Only)
      if (Data.info.legacyFormat) {
        str = `+${value} ${Locale.strings.ItemStast1k} ${skillName(param)} ${skillClass(param)}`
      } else {
        str = formatStr(str, value, skillName(param), skillClass(param));
      }
      break;
    case 28: // +[value] to [skill]
      //TODO: cap at 3 for appropriate class
      if (Data.info.legacyFormat) {
        str = `+${value} ${Locale.strings.ItemStast1k} ${skillName(param)}`
      } else {
        str = formatStr(str, value, skillName(param));
      }
      break;
    case 29:
      if (!isNaN(value) && parseInt(value) < 0) value = -value;
      str = formatStr(str, value);
      break;
    case 99: // Replenish quantity (hack)
      if (isNaN(value)) {
        m = value.match(/{(\d+)}/);
        if (m) value = `{${m[1]};div100}`;
      } else {
        value = (100 / value) | 0;
      }
      str = `${str} [1 in ${value} sec.]`;
      break;
      // no default
    }
    if (stat.descval === 1) {
      str = `${value} ${str}`;
    } else if (stat.descval === 2 && value != null) {
      str = `${str} ${value}`;
    }
    if (str2) {
      str = `${str} ${str2}`;
    }
    statLines.push([priority, str]);
  }

  statLines.sort((a, b) => b[0] - a[0]);
  for (const line of statLines) line[1] = line[1].trim();
  return statLines;
}

export function statsToLines(stats, charData) {
  const statLines = statsToLines0(stats, charData);
  if (stats.item_ethereal || stats.item_numsockets) {
    const mods = [];
    if (stats.item_ethereal) {
      mods.push(Locale.istrings[22745]);
    }
    if (stats.item_numsockets) {
      if (Data.info.legacyFormat) {
        mods.push(`${Locale.strings.Socketable} (${stats.item_numsockets})`);
      } else {
        mods.push(formatStr(Locale.strings.Socketable, stats.item_numsockets));
      }
    }
    statLines.push([0, mods.join(", ")]);
  }
  return statLines;
}

export function magicName(item) {
  let prefix = null;
  let suffix = null;
  for (const id in item.mods) {
    if (Data.magicPrefix[id]) {
      prefix = Locale.strings[Data.magicPrefix[id].name] || Data.magicPrefix[id].name;
    }
    if (Data.magicSuffix[id]) {
      suffix = Locale.strings[Data.magicSuffix[id].name] || Data.magicSuffix[id].name;
    }
  }
  const base = Data.items[item.base];
  let name = Locale.strings[base.namestr] || base.name;
  if (prefix) name = prefix + " " + name;
  if (suffix) name = name + " " + suffix;
  return name;
}

export function extendType(types, type) {
  function addType(id) {
    if (types.has(id)) return;
    const type = Data.itemTypes[id];
    if (!type) return;
    types.add(id);
    if (type.equiv1) addType(type.equiv1);
    if (type.equiv2) addType(type.equiv2);
  }
  addType(type);
  return types;
}

export function itemGetTypes(id) {
  const item = Data.items[id];
  const types = new Set();
  if (!item) return types;
  if (item.type) extendType(types, item.type);
  if (item.type2) extendType(types, item.type2);
  return types;
}

export function formatRanges(text, modmin, modmax, nopar) {
  return text.replace(/\({(\d+)}\)|(\+?){(\d+)(?:;(\w+))?}/g, (m, idx0, plus, idx, func) => {
    idx = parseInt(idx || idx0);
    let min = modmin.get(idx);
    let max = modmax.get(idx);
    let tmp;
    if (min == null || !isFinite(min) || max == null || !isFinite(max)) return '#';
    switch (func) {
    case 'skill':
      return '<skill>'; //TODO: locale
    case 'skillclass':
      return '(<class> Only)'; //TODO: locale
    case 'classskills':
      return 'to <class> Skill Levels'; //TODO: locale
    case 'per128':
      min = (min * 100 / 128) | 0;
      max = (max * 100 / 128) | 0;
      break;
    case 'div100':
      tmp = min;
      min = (100 / max) | 0;
      max = (100 / tmp) | 0;
      break;
    // no default
    }
    if (min === max) {
      if (max < 0) {
        return idx0 ? `(${max})` : max;
      } else {
        return (plus || '') + (idx0 ? `(${max})` : max);
      }
    } if (max < 0) {
      return `-(${-max}\u2013${-min})`;
    } else if (nopar && !idx0) {
      return `${plus || ''}${min}\u2013${max}`;
    } else {
      return `${plus || ''}(${min}\u2013${max})`;
    }
  });
}

export function itemCountMods(item, exclude) {
  const result = {prefix: 0, suffix: 0};
  for (const id in item.mods) {
    if (id === exclude) continue;
    if (Data.magicPrefix[id]) result.prefix += 1;
    if (Data.magicSuffix[id]) result.suffix += 1;
  }
  return result;
}

export function itemGetType(types) {
  //if (types.has('thro')) return 'thrown';
  if (types.has('scep')) return 'scepter';
  if (types.has('wand')) return 'wand';
  if (types.has('staf')) return 'staff';
  if (types.has('miss')) return 'bow';
  if (types.has('shld')) return 'shield';
  if (types.has('boot')) return 'boots';
  if (types.has('glov')) return 'gloves';
  if (types.has('belt')) return 'belt';
  if (types.has('weap')) return 'weapon';
  if (types.has('armo')) return 'armor';
}

export function itemCheckMod(mod, item, types, quality) {
  if (mod.level > 99) return false; // sorry, no 50 life GCs allowed
  if (mod.sockets && !itemMaxSockets(item)) return false;
  if (mod.armor != null) {
    // superior mods, they are checked in advance
    return true;
  } if (mod.input1) {
    const p = mod.input1.split(','), o = mod.output?.split(',');
    if (p.includes('sock')) return false; // we ignore socketed item corruptions for now
    const q = o?.includes('usetype') ? o : p;
    if (q.includes('nor') && quality !== Quality.NORMAL) return false;
    if (q.includes('hiq') && quality !== Quality.SUPERIOR) return false;
    if (q.includes('low') && quality !== Quality.LOW) return false;
    if (q.includes('mag') && quality !== Quality.MAGIC && quality !== Quality.CRAFTED) return false;
    if (q.includes('rar') && quality !== Quality.RARE) return false;
    if (q.includes('uni') && quality !== Quality.UNIQUE) return false;
    if (q.includes('set') && quality !== Quality.SET) return false;
    if (q.includes('crf') && quality !== Quality.CRAFTED) return false;
    if (p.includes('upg')) {
      if (item.normcode !== p[0] && item.ubercode !== p[0] && item.ultracode !== p[0]) return false;
    } else {
      if (item.code !== p[0] && !types.has(p[0])) return false;
    }
  } else {
    if (mod.classspecific) {
      const type = Data.itemTypes[item.type];
      if (type?.class && type.class !== mod.classspecific) return false;
    }
    if (![mod.itype1, mod.itype2, mod.itype3, mod.itype4, mod.itype5, mod.itype6, mod.itype7].some(t => t && types.has(t))) return false;
    if ([mod.etype1, mod.etype2, mod.etype3, mod.etype4, mod.etype5].some(t => t && types.has(t))) return false;
    if (!mod.rare && (quality === Quality.RARE || quality === Quality.CRAFTED)) return false;
  }
  return true;
}

export function itemGetModGroups(itemid, quality, ilvl=99, filter) {
  const item = Data.items[itemid];
  const types = itemGetTypes(itemid);

  function makeGroups(modList, type, gid, supt) {
    const mods = Object.entries(modList).filter(([id, mod]) => {
      if (gid != null && mod.group !== gid) return false;
      if (supt != null && !mod[supt]) return false;
      if (filter && !filter(mod)) return false;
      return itemCheckMod(mod, item, types, quality);
    });
    const groupMap = {};
    for (const [id, mod] of mods) {
      const stats = modsToStats({}, {[id]: ["{1}", "{2}", "{3}", "{4}", "{5}"]}, {base: itemid, ilvl});
      if (Object.keys(stats).length > 2) delete stats.corrupted;
      const lines = statsToLines(stats);
      const name = lines.map(line => line[1].replace(/\xffc\d/g, '')).join(" / ");
      const key = (mod.group || 0) + "#" + name;
      if (!groupMap[key]) {
        groupMap[key] = {
          name,
          type,
          group: mod.group || 0,
          prio: Math.max(...lines.map(line => line[0])),
          mods: [],
        };
      }
      groupMap[key].mods.push(id);
    }
    const groups = Object.values(groupMap);
    for (const g of groups) {
      const modmin = new Map([1, 2, 3, 4, 5].map(i => [i, Math.min(...g.mods.map(id => modList[id][`mod${i}min`]).filter(x => x != null))]));
      const modmax = new Map([1, 2, 3, 4, 5].map(i => [i, Math.max(...g.mods.map(id => modList[id][`mod${i}max`]).filter(x => x != null))]));
      g.name = formatRanges(g.name, modmin, modmax);
      g.mods.sort((a, b) => modList[a].level - modList[b].level);
    }
    return groups.sort((a, b) => b.prio - a.prio);
  }

  const result = {};
  if (quality === Quality.MAGIC || quality === Quality.RARE || quality === Quality.CRAFTED) {
    result.prefix = makeGroups(Data.magicPrefix, 'prefix');
    result.suffix = makeGroups(Data.magicSuffix, 'suffix');
  }
  if (item.autoprefix) {
    result.auto = makeGroups(Data.autoMagic, 'auto', item.autoprefix);
  }
  if (quality === Quality.CRAFTED) {
    result.crafted = makeGroups(Data.crafted, 'crafted');
  }
  if (quality === Quality.SUPERIOR || quality === Quality.RUNEWORD) {
    const supt = itemGetType(types);
    if (supt) result.superior = makeGroups(Data.qualityItems, 'superior', null, supt);
  }
  if (Data.itemTypes[item.type]?.staffmods && (quality !== Quality.UNIQUE && quality !== Quality.SET)) {
    result.staff = makeGroups(Data.staffMods, 'staff');
  }
  if (Data.info.pd2 && (quality === Quality.MAGIC || quality === Quality.RARE || quality === Quality.UNIQUE || quality === Quality.SET || quality === Quality.CRAFTED)) {
    result.corrupted = makeGroups(Data.corruptedMods, 'corrupted');
  }
  return result;
}

export function findModGroup(groups, id) {
  if (Data.magicPrefix[id]) {
    groups = groups.prefix || [];
  } else if (Data.magicSuffix[id]) {
    groups = groups.suffix || [];
  } else if (Data.autoMagic[id]) {
    groups = groups.auto || [];
  } else if (Data.crafted[id]) {
    groups = groups.crafted || [];
  } else if (Data.staffMods[id]) {
    groups = groups.staff || [];
  } else if (Data.corruptedMods?.[id]) {
    groups = groups.corrupted || [];
  } else {
    groups = [];
  }

  const group = groups.find(g => g.mods.includes(id));
  return group ? group : ({mods: [id], prio: 0});
}

export function findClosestMod(mods, values, index) {
  let bestId = null, bestDist = Infinity, bestDist0 = Infinity;
  for (const id of mods) {
    const mod = Data.mods[id];
    if (!mod) continue;
    const dist0 = Math.max(mod[`mod${index + 1}min`] - values[index], values[index] - mod[`mod${index + 1}max`], 0);
    if (dist0 <= bestDist0) {
      let dist = 0;
      for (let i = 0; i < values.length; ++i) {
        if (i === index) continue;
        if (mod[`mod${i + 1}min`] != null && values[i] < mod[`mod${i + 1}min`]) {
          dist += mod[`mod${i + 1}min`] - values[i];
        }
        if (mod[`mod${i + 1}max`] != null && values[i] > mod[`mod${i + 1}max`]) {
          dist += values[i] - mod[`mod${i + 1}max`];
        }
      }
      if (dist0 < bestDist0 || dist < bestDist) {
        bestId = id;
        bestDist0 = dist0;
        bestDist = dist;
      }
    }
  }
  return bestId;
}

export function findLowestMod(mods, values) {
  let bestId = null, bestLvl = Infinity, bestDist = Infinity;
  for (const id of mods) {
    const mod = Data.mods[id];
    if (!mod) continue;
    let dist  = 0;
    for (let i = 0; i < values.length; ++i) {
      if (mod[`mod${i + 1}min`] != null && values[i] < mod[`mod${i + 1}min`]) {
        dist += mod[`mod${i + 1}min`] - values[i];
      }
      if (mod[`mod${i + 1}max`] != null && values[i] > mod[`mod${i + 1}max`]) {
        dist += values[i] - mod[`mod${i + 1}max`];
      }
    }
    if (dist < bestDist || (dist === bestDist && mod.level < bestLvl)) {
      bestId = id;
      bestDist = dist;
      bestLvl = mod.level;
    }
  }
  return bestId;
}

export function itemGetName(item, noBase) {
  const base = Data.items[item.base];
  if (!base) return _L`Unknown item`;
  let name = (Locale.strings[base.namestr] || base.name).split('\n');
  name = name[name.length - 1];
  name = name.replace(/\xffc[\d:;<]/g, '');
  let title;
  let gemmed = '';
  if (item.socketedItems?.some(Boolean)) gemmed = Locale.strings.Gemmed + ' ';
  const prefix = (item.personalized ? `${item.personalized}'s ` : '');
  switch (item.quality) {
  case Quality.LOW:
    return `${gemmed}${prefix}${Locale.strings.Crude} ${name}`;
  case Quality.NORMAL:
    return `${gemmed}${prefix}${name}`;
  case Quality.SUPERIOR:
    return `${gemmed}${prefix}${Locale.strings.Hiquality} ${name}`;
  case Quality.MAGIC:
    return prefix + magicName(item);
  case Quality.RARE:
  case Quality.CRAFTED:
    return item.name ? `${prefix}${item.name} ${name}` : name;
  case Quality.SET:
  case Quality.UNIQUE:
  case Quality.RUNEWORD:
    title = (Data.uniqueItems[item.unique]?.index || Data.setItems[item.unique]?.index || Data.runes[item.unique]?.name);
    if (title) {
      title = Locale.strings[title] || title;
      if (item.unidentified) title = `[${title}]`;
      if (item.quality === Quality.RUNEWORD) {
        return `${title} ${prefix}${name}`;
      } else {
        if (base.skipname || noBase) {
          return prefix + title;
        } else {
          return `${prefix}${title} ${name}`;
        }
      }
    } else {
      return name;
    }
  default:
    return _L`Unknown item`;
  }
}

export function itemLevelReq(item, state, merc) {  
  const base = Data.items[item.base];
  if (!base) return 0;

  const profile = state && state.profiles[state.activeProfile];

  let value = base.levelreq || 0;
  if (item.unique) {
    const uniq = Data.uniqueItems[item.unique] || Data.setItems[item.unique];
    if (uniq) {
      value = Math.max(value, uniq.lvlreq || 0);
      const ubase = uniq.code || uniq.item;
      if (ubase !== item.base) {
        if (ubase === base.normcode && item.base === base.ubercode) {
          value += 5;
        } else if (ubase === base.ubercode && item.base === base.ultracode) {
          value += 7;
        } else if (ubase === base.normcode && item.base === base.ultracode) {
          value += 12;
        }
      }
    }
  }

  let modValue = 0;
  if (item.mods) {
    for (const id in item.mods) {
      const mod = Data.mods[id];
      if (!mod) continue;
      if (mod.class && !merc && mod.class === profile?.class && mod.classlevelreq) {
        modValue = Math.max(modValue, mod.classlevelreq);
      } else if (mod.levelreq) {
        modValue = Math.max(modValue, mod.levelreq);
      }
    }
  }
  if (item.quality === Quality.MAGIC && item.auto) {
    for (const id in item.auto) {
      const mod = Data.autoMagic[id];
      if (mod && mod.levelreq) {
        modValue = Math.max(modValue, mod.levelreq);
      }
    }
  }

  if (item.staff) {
    for (const id in item.staff) {
      const mod = Data.staffMods[id];
      if (mod && mod.levelreq) {
        modValue = Math.max(modValue, mod.levelreq);
      }
    }
  }

  if (item.quality === Quality.CRAFTED && item.mods) {
    modValue += 3 * Object.keys(item.mods).length;
  }
  value = Math.max(value, modValue);
  if (item.quality === Quality.CRAFTED) {
    value += 10;
  }
  value = Math.min(value, 98);

  if (item.socketedItems) {
    for (const id of item.socketedItems) {
      const sock = state?.items[id] || presetItems[id];
      if (sock) value = Math.max(value, itemLevelReq(sock, state));
    }
  }

  return value;
}

export function itemMinLevel(item) {
  const base = Data.items[item.base];
  if (!base) return 0;

  let value = 1; // do not use base req because items can be sold at shops with wrong level
  if (item.unique) {
    const uniq = Data.uniqueItems[item.unique] || Data.setItems[item.unique];
    if (uniq) {
      value = Math.max(value, uniq.lvl || 0);
    }
  }

  let affix = 0;
  if (item.mods) {
    for (const id in item.mods) {
      const mod = Data.mods[id];
      if (mod && mod.level) {
        affix = Math.max(affix, mod.level);
      }
    }
  }
  if (base.magiclvl) {
    affix -= base.magiclvl;
  } else if (affix < 99 - 2 * (base.level >> 1)) {
    affix += base.level >> 1;
  } else {
    affix = Math.ceil((affix + 99) / 2);
  }
  if (affix > base.level) {
    value = Math.max(value, affix);
  }

  if (item.auto) {
    for (const id in item.auto) {
      const mod = Data.autoMagic[id];
      if (mod && mod.levelreq) {
        value = Math.max(value, mod.level);
      }
    }
  }

  if (item.staff) {
    for (const id in item.staff) {
      const mod = Data.staffMods[id];
      if (mod && mod.levelreq) {
        value = Math.max(value, mod.level);
      }
    }
  }

  const type = Data.itemTypes[base.type];
  if (item.sockets && type) {
    if (item.sockets > type.maxsock1) {
      value = Math.max(value, 26);
    }
    if (item.sockets > type.maxsock25) {
      value = Math.max(value, 41);
    }
  }

  return Math.min(value, 99);
}

export function itemGetColor(item) {
  const base = Data.items[item.base];
  if (base && Locale.strings[base.namestr]?.match(/^\xffc[\d:;<]/)) return Locale.strings[base.namestr].charCodeAt(2) - 0x30;
  if (base && base.type === 'rune') return Color.ORANGE;
  if (base && base.quest) return Color.GOLD;
  if (base && base.type === 'ques') return Color.ORANGE;
  if (base && base.unique) return Color.GOLD;
  if (item.quality <= Quality.SUPERIOR && (item.sockets || item.ethereal)) return Color.GRAY;
  return Data.qualityColor[item.quality];
}

function itemGetIconHD(item) {
  const base = Data.items[item.base];
  if (!base) return null;

  let icon = base.hd;
  if (item.quality === Quality.UNIQUE || item.quality === Quality.SET) {
    icon = (Data.uniqueItems[item.unique] || Data.setItems[item.unique])?.hd;
    if (!icon) return null;
    if (!base.normcode || base.normcode === item.base) {
      icon = icon[0];
    } else if (base.ubercode === item.base) {
      icon = icon[1];
    } else if (base.ultracode === item.base) {
      icon = icon[2];
    } else {
      icon = icon[0];
    }
  }
  if (!icon) return null;

  const type = Data.itemTypes[base.type];
  if (type?.varinvgfx && icon === base.hd) {
    icon = icon + (((item.iconIndex || 0) % type.varinvgfx) + 1);
  }

  return icon;
}

export function itemGetIcon(item, hd) {
  if (hd) return itemGetIconHD(item);
  const base = Data.items[item.base];
  if (!base) return null;
  let icon = base.invfile;
  const type = Data.itemTypes[base.type];
  if (type?.varinvgfx) {
    icon = type[`invgfx${((item.iconIndex || 0) % type.varinvgfx) + 1}`] || icon;
  }

  if (item.quality === Quality.UNIQUE) {
    const uniq = Data.uniqueItems[item.unique];
    icon = uniq?.invfile || base.uniqueinvfile || icon;
  } else if (item.quality === Quality.SET) {
    const uniq = Data.setItems[item.unique];
    icon = uniq?.invfile || base.setinvfile || icon;
  }

  return icon;
}

const colorIndices = ['whit', 'lgry', 'dgry', 'blac', 'lblu', 'dblu', 'cblu', 'lred', 'dred', 'cred',
  'lgrn', 'dgrn', 'cgrn', 'lyel', 'dyel', 'lgld', 'dgld', 'lpur', 'dpur', 'oran', 'bwht'];

export function itemGetTint(item) {
  const base = Data.items[item.base];
  if (!base) return 0;
  let color = null;
  let colorIndex = -1;

  if (item.quality === Quality.UNIQUE) {
    const uniq = Data.uniqueItems[item.unique];
    color = uniq?.invtransform;
  } else if (item.quality === Quality.SET) {
    const uniq = Data.setItems[item.unique];
    color = uniq?.invtransform;
  } else if (item.quality !== Quality.RUNEWORD && item.quality !== Quality.CRAFTED) {
    if (item.mods) {
      for (const id in item.mods) {
        const mod = Data.mods[id];
        if (mod?.transformcolor) color = mod.transformcolor;
      }
    }
    if (item.auto) {
      for (const id in item.auto) {
        const mod = Data.mods[id];
        if (mod?.transformcolor) color = mod.transformcolor;
      }
    }
    if (item.socketedItems) {
      for (const id of item.socketedItems) {
        const gem = Data.gems[id];
        if (gem?.transform && !gem.letter) {
          colorIndex = gem.transform;
        }
      }
    }
  }

  if (colorIndex < 0) colorIndex = colorIndices.indexOf(color);

  return (colorIndex >= 0 ? (base.invtrans % 10) * 21 + colorIndex : 0);
}

export function randomRareName(types) {
  function filter(row) {
    if (![row.itype1, row.itype2, row.itype3, row.itype4, row.itype5, row.itype6, row.itype7].some(t => t && types.has(t))) return false;
    if ([row.etype1, row.etype2, row.etype3, row.etype4].some(t => t && types.has(t))) return false;
    return true;
  }
  let prefix = Data.rarePrefix.filter(filter);
  let suffix = Data.rareSuffix.filter(filter);
  prefix = prefix[Math.floor(Math.random() * prefix.length)]?.name;
  suffix = suffix[Math.floor(Math.random() * suffix.length)]?.name;
  return (Locale.strings[prefix] || prefix) + ' ' + (Locale.strings[suffix] || suffix);
}

export function itemMaxSockets(base, quality) {
  if (!base || !base.hasinv) return 0;

  let boxSockets = 0;
  if (Data.info.pd2) {
    const types = itemGetTypes(base.code);
    if (types.has('2han')) boxSockets = 4;
    else if (types.has('tors') || types.has('shld') || types.has('helm') || types.has('weap')) boxSockets = 2;
  }
  
  const type = Data.itemTypes[base.type];
  const maxsockets = Math.min(base.gemsockets, type.maxsock40, base.invwidth * base.invheight);
  switch (quality) {
  case Quality.MAGIC:
    return Math.min(Math.max(boxSockets, 2), maxsockets);
  case Quality.RARE:
  case Quality.SET:
  case Quality.UNIQUE:
  case Quality.CRAFTED:
    return Math.min(Math.max(boxSockets, 1), maxsockets);
  default:
    return maxsockets; // we ignore ilvl requirements here because users aren't expected to set correct ilvl
  }
}

export function itemDefenseRange(item) {
  const base = Data.items[item.base];
  if (!base || !base.maxac) return null;
  let {minac, maxac} = base;
  switch (item.quality) {
  case Quality.SUPERIOR:
  case Quality.MAGIC:
    // item spawned with AC% and could not have been upgraded
    if (item.stats?.item_armor_percent) {
      minac = maxac = maxac + 1;
    }
    break;
  case Quality.RARE:
    // item spawned with AC%, but it might have been cube upgraded, which randomizes the defense
    if (item.stats?.item_armor_percent) {
      maxac += 1;
    }
    break;
  case Quality.SET:
    {
      const set = Data.setItems[item.unique];
      if ([set.aprop1a, set.aprop1b, set.aprop2a, set.aprop2b, set.aprop3a, set.aprop3b, set.aprop4a, set.aprop4b, set.aprop5a, set.aprop5b].includes('ac%')) {
        minac = maxac = maxac + 1;
        break;
      }
    }
    // fallthrough
  case Quality.UNIQUE:
    // same as rare but we can actually find out if item was upgraded
    if ((Data.uniqueItems[item.unique]?.code || Data.setItems[item.unique]?.item) === item.base) {
      if (item.stats?.item_armor_percent) {
        minac = maxac = maxac + 1;
      }
    }
    break;
  case Quality.RUNEWORD:
    // same as superior but we need to check stats manually
    if (item.superior) {
      for (const id in item.superior) {
        const mod = Data.qualityItems[id];
        if (mod && (mod.mod1code === 'ac%' || mod.mod2code === 'ac%')) {
          minac = maxac = maxac + 1;
        }
      }
    }
    break;
  // no default
  }
  return {minac, maxac};
}

export function itemAffixLevel(id, ilvl) {
  const item = Data.items[id];
  if (!item) return ilvl;
  let x = Math.max(item.level, ilvl);
  if (item.magiclvl) {
    x += item.magiclvl;
  } else if (x < 99 - (item.level >> 1)) {
    x -= (item.level >> 1);
  } else {
    x += x - 99;
  }
  return Math.min(99, x);
}

export function baseNoDurability(base, quality) {
  if (!base.nodurability) return false;
  if (quality !== Quality.RARE && quality !== Quality.UNIQUE && (quality !== Quality.SET || !Data.info.options.setUpgrade)) return true;
  const prev = [base.normcode, base.ubercode, base.ultracode].filter(Boolean);
  if (!prev.length) return true;
  const curIndex = prev.indexOf(base.code);
  if (curIndex >= 0) prev.length = curIndex;
  return !prev.some(id => !Data.items[id]?.nodurability);
}
